import { Injectable, Inject, Optional } from '@angular/core';
import { Analytics, logEvent, setUserProperties, setUserId, setAnalyticsCollectionEnabled } from '@angular/fire/analytics';
import { environment } from 'src/environments/environment';

export interface EventParameters {
  [key: string]: unknown;
}

export interface ConversionParameters extends EventParameters {
  value: number;
  currency?: string;
}

export interface AnalyticsEvent {
  name: string;
  parameters: EventParameters;
  timestamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly APP_ID = 'signup'; // Hardcoded app_id for signup flow
  private readonly MAX_EVENTS = 50; // Maximum number of events to store
  private startTime: number;
  private pageStartTimes: Map<string, number> = new Map();
  private isEnabled = true;
  private debugMode = !environment.production;
  private eventHistory: AnalyticsEvent[] = [];

  constructor(@Optional() @Inject('ANALYTICS') private analytics: Analytics) {
    this.startTime = Date.now();

    if (!this.analytics && !environment.production) {
      console.warn('Analytics not initialized. Events will be logged to console only.');
    }

    // Set app_id as a user property for long-term segmentation
    if (this.analytics) {
      setUserProperties(this.analytics, { app_id: this.APP_ID });
    }

    // Check for debug flag in localStorage or environment
    const enableAnalyticsDebug = localStorage.getItem('ENABLE_ANALYTICS_DEBUG') === 'true';
    if (enableAnalyticsDebug || environment.name === 'development') {
      this.setDebugMode(true);
      console.log('🔍 Analytics Debug Mode: ENABLED (sending to Firebase)');
    }

    // Enable/disable analytics collection based on environment and debug mode
    if (this.analytics) {
      const shouldEnable = environment.production || this.debugMode;
      setAnalyticsCollectionEnabled(this.analytics, shouldEnable);

      if (!environment.production) {
        console.log(`📊 Analytics Collection: ${shouldEnable ? 'ENABLED' : 'DISABLED'}`);
        console.log('💡 To enable analytics in development, run: localStorage.setItem("ENABLE_ANALYTICS_DEBUG", "true") and refresh');
      }
    }
  }

  /**
   * Track a custom event
   */
  trackEvent(eventName: string, parameters?: EventParameters): void {
    if (!this.isEnabled) return;

    // Add common parameters including app_id
    const enrichedParams = {
      ...parameters,
      app_id: this.APP_ID,
      environment: environment.production ? 'production' : 'development'
    };

    // Store event in history for debug panel
    this.addToEventHistory(eventName, enrichedParams);

    if (this.debugMode || !this.analytics) {
      console.log(`🔍 Analytics Event: ${eventName}`, enrichedParams);
    }

    if (this.analytics && (environment.production || this.debugMode)) {
      try {
        logEvent(this.analytics, eventName, enrichedParams);
      } catch (error) {
        console.error('Failed to log analytics event:', error);
      }
    }
  }

  /**
   * Track a conversion event
   */
  trackConversion(eventName: string, value: number, parameters?: EventParameters): void {
    this.trackEvent(eventName, {
      ...parameters,
      value,
      currency: parameters?.['currency'] || 'USD',
      conversion: true
    });
  }

  /**
   * Track form errors
   */
  trackFormError(fieldName: string, errorType: string, pageName: string, errorMessage?: string): void {
    this.trackEvent('form_error', {
      field_name: fieldName,
      error_type: errorType,
      page_name: pageName,
      error_message: errorMessage || errorType
    });
  }

  /**
   * Track plan selection
   */
  trackPlanSelection(planName: string, price: number, billingFrequency: 'monthly' | 'annual'): void {
    this.trackEvent('plan_selected', {
      plan_name: planName,
      plan_price: price,
      billing_frequency: billingFrequency,
      selection_time_ms: this.getTotalTime()
    });
  }

  /**
   * Track venue type selection
   */
  trackVenueTypeSelection(venueType: string, venueSubtype?: string): void {
    this.trackEvent('venue_type_selected', {
      venue_type: venueType,
      venue_subtype: venueSubtype,
      selection_time_ms: this.getTotalTime()
    });
  }

  /**
   * Track signup completion
   */
  trackSignupCompletion(venueData: Record<string, unknown>, planName: string, billingFrequency: string, totalTimeMs?: number): void {
    this.trackConversion('signup_completed', this.getPlanValue(planName, billingFrequency), {
      venue_type: venueData['venueType'],
      venue_subtype: venueData['venueSubtype'],
      plan_name: planName,
      billing_frequency: billingFrequency,
      total_time_ms: totalTimeMs ?? this.getTotalTime(),
      user_locale: venueData['userLocale'] || 'en-US'
    });
  }

  /**
   * Track API errors
   */
  trackApiError(apiName: string, errorCode: string, retryCount = 0): void {
    this.trackEvent('api_error', {
      api_name: apiName,
      error_code: errorCode,
      retry_count: retryCount
    });
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, unknown>): void {
    if (!this.analytics || !this.isEnabled) return;

    const sanitizedProperties = this.sanitizeUserProperties(properties);

    if (this.debugMode) {
      console.log('🔍 Setting user properties:', sanitizedProperties);
    }

    if (this.analytics && (environment.production || this.debugMode)) {
      try {
        setUserProperties(this.analytics, sanitizedProperties);
      } catch (error) {
        console.error('Failed to set user properties:', error);
      }
    }
  }

  /**
   * Set user ID (for logged-in users)
   */
  setUserId(userId: string): void {
    if (!this.analytics || !this.isEnabled) return;

    if (this.debugMode) {
      console.log('🔍 Setting user ID:', userId);
    }

    if (this.analytics && (environment.production || this.debugMode)) {
      try {
        setUserId(this.analytics, userId);
      } catch (error) {
        console.error('Failed to set user ID:', error);
      }
    }
  }

  /**
   * Start tracking time on a page
   */
  startPageTimer(pageName: string): void {
    this.pageStartTimes.set(pageName, Date.now());
  }

  /**
   * Get time spent on a page
   */
  getTimeOnPage(pageName: string): number {
    const startTime = this.pageStartTimes.get(pageName);
    if (!startTime) return 0;
    return Date.now() - startTime;
  }

  /**
   * Get total time since service initialization
   */
  getTotalTime(): number {
    return Date.now() - this.startTime;
  }

  /**
   * Enable/disable analytics
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    
    if (this.analytics) {
      setAnalyticsCollectionEnabled(this.analytics, enabled);
    }
  }

  /**
   * Enable/disable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    
    // Save preference to localStorage
    localStorage.setItem('ENABLE_ANALYTICS_DEBUG', enabled ? 'true' : 'false');
    
    if (this.analytics) {
      setAnalyticsCollectionEnabled(this.analytics, environment.production || enabled);
      console.log(`📊 Analytics Collection: ${enabled ? 'ENABLED' : 'DISABLED'}`);
    }
  }

  /**
   * Check if analytics is currently enabled
   */
  isAnalyticsEnabled(): boolean {
    return this.isEnabled && (environment.production || this.debugMode);
  }

  /**
   * Get the app ID for this application
   */
  getAppId(): string {
    return this.APP_ID;
  }

  /**
   * Get recent analytics events for debugging
   */
  getRecentEvents(): AnalyticsEvent[] {
    return [...this.eventHistory].reverse(); // Most recent first
  }

  /**
   * Get debug mode status
   */
  isDebugMode(): boolean {
    return this.debugMode;
  }

  /**
   * Clear event history
   */
  clearEventHistory(): void {
    this.eventHistory = [];
  }

  /**
   * Add event to history (for debug panel)
   */
  private addToEventHistory(eventName: string, parameters: EventParameters): void {
    const event: AnalyticsEvent = {
      name: eventName,
      parameters: { ...parameters },
      timestamp: new Date()
    };

    this.eventHistory.push(event);

    // Keep only last MAX_EVENTS
    if (this.eventHistory.length > this.MAX_EVENTS) {
      this.eventHistory.shift();
    }
  }

  /**
   * Sanitize user properties to remove PII
   */
  private sanitizeUserProperties(properties: Record<string, unknown>): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};
    
    for (const [key, value] of Object.entries(properties)) {
      // Skip sensitive fields
      if (['email', 'password', 'phone', 'ssn', 'credit_card'].includes(key.toLowerCase())) {
        continue;
      }
      
      // Sanitize email to domain only
      if (key.toLowerCase().includes('email') && typeof value === 'string' && value.includes('@')) {
        sanitized[key] = value.split('@')[1];
      } else {
        sanitized[key] = value;
      }
    }
    
    return sanitized;
  }

  /**
   * Get plan value for conversion tracking
   */
  private getPlanValue(planName: string, billingFrequency: string): number {
    // This should match your actual pricing
    const planPrices: { [key: string]: { monthly: number; annual: number } } = {
      starter: { monthly: 29, annual: 290 },
      nonprofit: { monthly: 29, annual: 290 },
      essential: { monthly: 59, annual: 590 },
      enterprise: { monthly: 99, annual: 990 }
    };

    const plan = planPrices[planName.toLowerCase()];
    if (!plan) return 0;

    return billingFrequency === 'annual' ? plan.annual : plan.monthly;
  }
}